import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLinkIcon,
  RefreshIcon,
  StarIcon,
  TrashIcon,
  XIcon,
} from "@heroicons/react/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/solid";
import VSolonCard from "../../components/Cards/VSolonCard";
import useCaseDetail from "../../features/cases/useCaseDetail";
import { isCheckActive } from "../../features/cases/caseFilters";
import { truncateCourt } from "../../utils/truncateCourt";
import { caseDocumentTitle } from "../../components/SingleCase/ViewCase";

const isFav = (c) => c?.fav === 1 || c?.fav === true;

// Case details rendered next to the table. `rowCase` is the list entry: it
// fills the header immediately and its check state drives refreshes.
const CaseDetailPanel = ({
  caseId,
  rowCase,
  pendingCheck,
  onClose,
  onToggleFav,
  onRecheck,
  onDelete,
}) => {
  const refreshKey = `${rowCase?.last_checked ?? ""}|${rowCase?.check_status?.state ?? ""}`;
  const { data, updates, history, historyError, loading, error } = useCaseDetail(caseId, {
    refreshKey,
  });
  const bodyRef = useRef(null);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [caseId]);

  useEffect(() => {
    if (data) document.title = caseDocumentTitle(data);
  }, [data]);

  const headerCase = rowCase ?? data;
  const checking = pendingCheck || isCheckActive(rowCase);
  const title = headerCase?.note?.trim()
    ? headerCase.note
    : headerCase
    ? `ΓΑΚ ${headerCase.gak}/${headerCase.year}`
    : "";
  const subtitle = headerCase
    ? [
        headerCase.note?.trim() ? `ΓΑΚ ${headerCase.gak}/${headerCase.year}` : null,
        headerCase.court ? truncateCourt(headerCase.court) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <section className="ds-panel" aria-label="Στοιχεία υπόθεσης">
      <div className="ds-panel-header">
        <div className="ds-panel-heading">
          <div className="ds-panel-title" title={title}>
            {title}
          </div>
          {subtitle && <div className="ds-panel-subtitle">{subtitle}</div>}
        </div>
        <div className="ds-panel-actions">
          {headerCase && (
            <>
              <button
                type="button"
                className={`ds-icon-btn ds-star ${isFav(headerCase) ? "is-fav" : ""}`}
                onClick={() => onToggleFav(caseId)}
                aria-pressed={isFav(headerCase)}
                aria-label="Σημαντικό"
                title="Σημαντικό ( s )"
              >
                {isFav(headerCase) ? <StarIconSolid /> : <StarIcon />}
              </button>
              <button
                type="button"
                className="ds-icon-btn"
                onClick={() => onRecheck(caseId)}
                disabled={checking}
                aria-label="Έλεγχος τώρα"
                title={checking ? "Γίνεται έλεγχος…" : "Έλεγχος τώρα ( r )"}
              >
                <RefreshIcon className={checking ? "ds-spin" : ""} />
              </button>
              <button
                type="button"
                className="ds-icon-btn ds-icon-btn--danger"
                onClick={() => onDelete(caseId)}
                aria-label="Διαγραφή"
                title="Διαγραφή"
              >
                <TrashIcon />
              </button>
            </>
          )}
          <Link
            to={`/cases/${caseId}`}
            className="ds-icon-btn"
            aria-label="Πλήρης σελίδα"
            title="Πλήρης σελίδα ( o )"
          >
            <ExternalLinkIcon />
          </Link>
          <button
            type="button"
            className="ds-icon-btn"
            onClick={onClose}
            aria-label="Κλείσιμο"
            title="Κλείσιμο ( Esc )"
          >
            <XIcon />
          </button>
        </div>
      </div>
      <div className="ds-panel-body" ref={bodyRef}>
        {loading ? (
          <div className="ds-panel-placeholder">
            <span className="ds-spinner" aria-hidden="true" /> Ανακτώνται τα δεδομένα…
          </div>
        ) : error ? (
          <div className="ds-panel-placeholder ds-panel-error">
            {error?.response?.status === 404
              ? "Δεν βρέθηκε η υπόθεση."
              : "Δεν ήταν δυνατή η φόρτωση της υπόθεσης."}
          </div>
        ) : data ? (
          <VSolonCard
            key={data.id}
            variant="panel"
            singleCase={data}
            singleUpdates={updates}
            historyData={history}
            historyError={historyError}
            historyLoading={history === undefined}
          />
        ) : (
          <div className="ds-panel-placeholder">Δεν βρέθηκε η υπόθεση.</div>
        )}
      </div>
    </section>
  );
};

export default CaseDetailPanel;
